import classNames from './GenerateReport.module.scss';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { ClassReportContent } from '@/types/generate-report';
import { TeacherGroup } from '@/types/user';
import { UserStatisticsResponseData } from '@/school-management/types/response';
import Header from './Header/Header';
import TopCard from './TopCard/TopCard';
import Card from './Card/Card';
import ProgressCart from './ProgressCart/ProgressCart';
import BarGraph from './BarGraph/BarGraph';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import * as helper from './helper';
import common from '@/utils/common';
import { reportService } from '@/services/report';
import { adminManagementService } from '@/admin/services/adminManagement';
import { formatMinutes } from '@/utils/formatters';
import reportProgressIcon from '@/assets/svg/reportProgress.svg';
import activitiesCompletedIcon from '@/assets/svg/activities-completed.svg';
import passRateIcon from '@/assets/svg/pass-rate.svg';
import failureRateIcon from '@/assets/svg/failure-rate.svg';
import victoryTrophyIcon from '@/assets/svg/victory-trophy.svg';
import medalIcon from '@/assets/svg/medal.svg';
import clockIcon from '@/assets/svg/clock.svg';
import streakLengthIcon from '@/assets/svg/streakLength.svg';
import attemptsIcon from '@/assets/svg/attempts.svg';
import skillScoresIcon from '@/assets/svg/score.svg';
import timeSpentIcon from '@/assets/svg/gray-clock.svg';

export interface PreFetchedClassReportData {
  classReport: ClassReportContent;
  dashboardStatistics: UserStatisticsResponseData | null;
  classroomInfo: { name: string; grade: string; totalStudents: string };
  levels?: { id: number; name: string }[];
  interfaceLanguage?: string;
}

interface ClassReportProps {
  currentClassroom?: TeacherGroup;
  /** Whena Admin report is providing data - no calls needed */
  preFetchedData?: PreFetchedClassReportData;
  generateReportPdf: (element: HTMLElement | null) => void;
  onLoadReportError: () => void;
}

const ClassReport = ({
  currentClassroom,
  preFetchedData,
  generateReportPdf,
  onLoadReportError,
}: ClassReportProps) => {
  const [classReport, setClassReport] = useState<ClassReportContent | null | undefined>();
  const [dashboardStatistics, setDashboardStatistics] = useState<UserStatisticsResponseData | null>(
    null
  );
  const { t } = useTranslation();
  const {
    levels: storeLevels,
    user,
    interfaceLanguage: storeInterfaceLanguage,
  } = useAppSelector(authSelector);
  const isGoogle = common.isGoogle(user);

  const levels = preFetchedData?.levels ?? storeLevels;
  const interfaceLanguage = preFetchedData?.interfaceLanguage ?? storeInterfaceLanguage ?? '';

  const effectiveClassReport = preFetchedData ? preFetchedData.classReport : classReport;
  const effectiveDashboardStatistics = preFetchedData
    ? preFetchedData.dashboardStatistics
    : dashboardStatistics;

  useEffect(() => {
    if (preFetchedData) {
      return;
    }

    let isActive = true;

    const loadReport = async () => {
      if (!currentClassroom) {
        return;
      }

      try {
        const [classReportResponse, dashboardStatsResponse] = await Promise.all([
          reportService.fetchClassReport(currentClassroom.id, user),
          adminManagementService.getUserStatistics({
            groupIds: [currentClassroom.id],
          }),
        ]);

        if (isActive) {
          setClassReport(classReportResponse?.data?.data);
          if (dashboardStatsResponse?.data?.data) {
            setDashboardStatistics(dashboardStatsResponse.data.data);
          }
        }
      } catch (error) {
        if (isActive) {
          setClassReport(null);
          onLoadReportError();
        }
      }
    };
    loadReport();

    return () => {
      isActive = false;
    };
  }, [currentClassroom, user, onLoadReportError, preFetchedData]);

  const currentClassroomData = useMemo(() => {
    if (preFetchedData) {
      return preFetchedData.classroomInfo;
    }
    const name = currentClassroom?.name || '-';
    const grade = !currentClassroom?.grade ? '-' : t(`grade_${currentClassroom.grade}`);
    const totalStudents = currentClassroom?.totalStudents?.toString() || '-';
    return {
      name,
      grade,
      totalStudents,
    };
  }, [currentClassroom, t, preFetchedData]);

  const reportData = useMemo(() => {
    if (!effectiveClassReport) {
      return null;
    }

    const avgActivitiesCompleted = `${common.roundNumber(effectiveClassReport?.averageActivitiesCompleted || 0, 2)}`;
    const avgPassRate = `${common.roundNumber(effectiveClassReport?.passRate || 0, 0)}%`;
    const avgFailureRate = `${common.roundNumber(effectiveClassReport?.failureRate || 0, 0)}%`;
    const activeStudents = effectiveClassReport?.numberOfStudents || 0;
    const avgCurrentLevel =
      effectiveDashboardStatistics?.averageCurrentLevel !== undefined &&
      effectiveDashboardStatistics?.averageCurrentLevel !== null
        ? `${effectiveDashboardStatistics.averageCurrentLevel}`
        : '-';
    const avgScore =
      effectiveDashboardStatistics?.averageScore !== undefined &&
      effectiveDashboardStatistics?.averageScore !== null
        ? `${Math.round(effectiveDashboardStatistics.averageScore)}`
        : '-';
    const totalTimeSpent =
      effectiveDashboardStatistics?.totalTimeSpent !== undefined &&
      effectiveDashboardStatistics?.totalTimeSpent !== null
        ? formatMinutes(effectiveDashboardStatistics.totalTimeSpent)
        : '-';

    return {
      avgActivitiesCompleted,
      avgPassRate,
      avgFailureRate,
      activeStudents,
      avgCurrentLevel,
      avgScore,
      totalTimeSpent,
    };
  }, [effectiveClassReport, effectiveDashboardStatistics]);

  const reportLevelData = useMemo(() => {
    if (!effectiveClassReport || !levels) {
      return null;
    }

    const levelsSort = [...(levels || [])]
      .filter(level => level.id < 100 && (level.name || '').toLowerCase() !== 'level test')
      .sort((level1, level2) => level1.id - level2.id);

    // Longer locale strings (e.g. es) can wrap the "all levels avg time" row onto a
    // second line; this buffer keeps that row inside the card instead of overflowing it.
    const allLevelsAvgTimeRowBuffer = 12;
    const cardHeight = `${95 + 24 * (levelsSort.length + 1) - 13 + allLevelsAvgTimeRowBuffer}px`;
    const smallerCardHeight = `${95 + 24 * levelsSort.length - 13 - 12}px`;

    const avgStudentProgressMap = (
      effectiveClassReport?.averageSudentsPrograssPerLevel || []
    ).reduce((map, item) => {
      map.set(item.level, item.averageSudentsPrograss);
      return map;
    }, new Map<number, number>());

    const avgStudentProgressItems: {
      label: string;
      value: number;
      color: string;
    }[] = [];

    const avgTimeSpentMap = (effectiveClassReport?.averageTimeSpentPerLevels || []).reduce(
      (map, item) => {
        map.set(item.level, item.averageTimeSpent);
        return map;
      },
      new Map<number, number>()
    );

    const avgTimeSpentItems: {
      label: string;
      value?: string;
    }[] = [];

    const avgStreakLengthMap = (effectiveClassReport?.averageStreakLengthPerLevel || []).reduce(
      (map, item) => {
        map.set(item.level, item.averageStreakLength);
        return map;
      },
      new Map<number, number>()
    );

    const avgStreakLengthItems: {
      label: string;
      value?: string;
    }[] = [];

    const avgAttemptsMap = (effectiveClassReport?.averageActivityAttemptsPerLevel || []).reduce(
      (map, item) => {
        map.set(item.level, item.averageActivityAttempts);
        return map;
      },
      new Map<number, number>()
    );

    const avgAttemptsItems: {
      label: string;
      value?: string;
    }[] = [];

    levelsSort.forEach((level, levelIndex) => {
      const label = `${t('level')} ${levelIndex + 1}`;

      avgStudentProgressItems.push({
        label,
        value: avgStudentProgressMap.get(level.id) || 0,
        color: helper.levelColors[(level.id - 1) % helper.levelColors.length],
      });

      avgTimeSpentItems.push({
        label,
        value: helper.timeString(avgTimeSpentMap.get(level.id) || 0),
      });

      avgStreakLengthItems.push({
        label,
        value: common.formatString(
          t('reports.daysPerWeek'),
          common.roundNumber(avgStreakLengthMap.get(level.id) || 0, 2)
        ),
      });

      avgAttemptsItems.push({
        label,
        value: common.roundNumber(avgAttemptsMap.get(level.id) || 0, 2).toString(),
      });
    });

    const allTimeSpentValues = Array.from(avgTimeSpentMap.values());
    const totalTimeSpent = allTimeSpentValues.reduce((sum, time) => sum + time, 0);
    const avgTimeSpent =
      allTimeSpentValues.length > 0 ? totalTimeSpent / allTimeSpentValues.length : 0;

    avgTimeSpentItems.push({
      label: t('reports.allLevelsAvgTime') || "All level's avg. time",
      value: helper.timeString(avgTimeSpent),
    });

    const averageProgress = effectiveClassReport?.averageStudentProgress || 0;

    return {
      cardHeight,
      smallerCardHeight,
      avgStudentProgressItems,
      avgTimeSpentItems,
      avgStreakLengthItems,
      avgAttemptsItems,
      averageProgress,
    };
  }, [levels, effectiveClassReport, t]);

  const reportSkillsData = useMemo(() => {
    if (!effectiveClassReport) {
      return null;
    }

    const avgSkillScoresMap = (effectiveClassReport?.averageSkillScore || []).reduce(
      (map, item) => {
        map.set(item.skillId, item.averageScore);
        return map;
      },
      new Map<number, number>()
    );

    const avgSkillScoresItems: {
      label: string;
      value: number;
    }[] = [];

    helper.reportSkillsList.forEach(skill => {
      const label = t(`skills.${skill.name?.toLowerCase()}`);

      avgSkillScoresItems.push({
        label,
        value: common.roundNumber(avgSkillScoresMap.get(skill.id) || 0, 0),
      });
    });

    return {
      avgSkillScoresItems,
    };
  }, [effectiveClassReport, t]);

  const headerItems = useMemo(() => {
    const classAndGradeValue = isGoogle
      ? currentClassroomData.name
      : `${currentClassroomData.name} | ${currentClassroomData.grade}`;

    const teachersValue =
      effectiveClassReport?.teachers && effectiveClassReport.teachers.length > 0
        ? effectiveClassReport.teachers.map(teacher => `${teacher.firstName} ${teacher.lastName}`)
        : ['-'];

    return [
      { label: `${t('className')} & ${t('grade')}`, value: classAndGradeValue },
      { label: 'Teacher', value: teachersValue },
      { label: t('reports.totalStudents'), value: currentClassroomData.totalStudents },
      { label: t('date'), value: helper.dateString(new Date(), interfaceLanguage || '') },
    ];
  }, [currentClassroomData, effectiveClassReport, isGoogle, t, interfaceLanguage]);

  return (
    reportData &&
    reportLevelData &&
    reportSkillsData && (
      <div ref={generateReportPdf} className={classNames.pdfContainer}>
        <Header items={headerItems} />
        <div className={classNames.contentContainer}>
          <TopCard
            items={[
              {
                icon: activitiesCompletedIcon,
                label: t('reports.avgActivitiesCompleted'),
                value: reportData.avgActivitiesCompleted,
              },
              {
                icon: passRateIcon,
                label: t('reports.avgPassRate'),
                value: reportData.avgPassRate,
              },
              {
                icon: failureRateIcon,
                label: t('reports.avgFailureRate'),
                value: reportData.avgFailureRate,
              },
              {
                icon: victoryTrophyIcon,
                label: t('avgCurrentLevel'),
                value: reportData.avgCurrentLevel,
              },
              {
                icon: medalIcon,
                label: t('avgScore'),
                value: reportData.avgScore,
              },
              {
                icon: clockIcon,
                label: t('totalTimeSpent'),
                value: reportData.totalTimeSpent,
              },
            ]}
          />
          <div className={classNames.lineContainer}>
            <ProgressCart
              width='356px'
              height={reportLevelData.cardHeight}
              titleIcon={reportProgressIcon}
              title={t('reports.avgStudentProgress')}
              description={t('reports.perLevel')}
              items={reportLevelData.avgStudentProgressItems}
              averageProgress={reportLevelData.averageProgress}
            />
            <Card
              width='176px'
              height={reportLevelData.cardHeight}
              titleIcon={timeSpentIcon}
              title={t('reports.avgTimeSpent')}
              description={t('reports.onActivities')}
              items={reportLevelData.avgTimeSpentItems}
            />
          </div>
          <div className={classNames.lineContainer}>
            <Card
              width='266px'
              height={reportLevelData.smallerCardHeight}
              titleIcon={streakLengthIcon}
              title={t('reports.avgStreakLength')}
              description={t('reports.continuousEngagement')}
              items={reportLevelData.avgStreakLengthItems}
            />
            <Card
              width='266px'
              height={reportLevelData.smallerCardHeight}
              titleIcon={attemptsIcon}
              title={t('reports.avgAttempts')}
              description={t('reports.perActivity')}
              items={reportLevelData.avgAttemptsItems}
            />
          </div>
          <BarGraph
            width='552px'
            height='204px'
            titleIcon={skillScoresIcon}
            title={t('reports.avgSkillScores')}
            description={t('reports.averagePerSkill') || undefined}
            items={reportSkillsData.avgSkillScoresItems}
          />
          <div className={classNames.remark}>
            <samp
              className={classNames.text}
              dangerouslySetInnerHTML={{
                __html:
                  common.formatString(
                    t('reports.remark'),
                    reportData.activeStudents,
                    t('reports.engagedStudentsWord', { count: reportData.activeStudents }),
                    currentClassroomData.totalStudents,
                    t('reports.studentsOnlyWord', {
                      count: Number(currentClassroomData.totalStudents),
                    })
                  ) +
                  '<br/>' +
                  '<br/>' +
                  t('reports.skippedActivitiesComment'),
              }}
            />
          </div>
        </div>
      </div>
    )
  );
};

export default ClassReport;
